/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * Module containing Common UI components and Core services.
 * @preferred
 * @module SharedModule
 */

import {
    CUSTOM_ELEMENTS_SCHEMA,
    NgModule,
    NO_ERRORS_SCHEMA,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AviPipesModule, AviTooltipModule } from 'nsx-alb-ui-components';
import {
    ClrAlertModule,
    ClrCheckboxModule,
    ClrDatalistModule,
    ClrDropdownModule,
    ClrIconModule,
    ClrInputModule,
    ClrProgressBarModule,
    ClrRadioModule,
    ClrSpinnerModule,
} from '@clr/angular';
import { VIPModule } from '@vmw/ngx-vip';
import { LetModule, PushModule } from '@ngrx/component';
import { TooltipModule } from 'ng/modules/tooltip/tooltip.module';
import { RightRailModule } from 'ng/modules/right-rail/right-rail.module';
import { AviRadioModule } from 'ng/modules/avi-forms/modules/avi-radio/avi-radio.module';

import {
    AviDropdownComponent,
    AviDropdownOptionsComponent,
    AviDropdownSingleSelectedValueComponent,
} from 'standalone-components/avi-dropdown';

import { AviInputContainerComponent } from 'standalone-components/avi-input-container';
import { TimeframeSelectorComponent } from 'standalone-components/timeframe-selector';
import { MetricValuesSelectorComponent } from 'standalone-components/metric-values-selector';

import {
    BooleanColorDirective,
    InputFieldDirective,
    SetBreadcrumbDescriptionDirective,
} from './directives';

import {
    AsyncFileUploadComponent,
    AsyncFileUploadWithUploadServiceComponent,
    AviAlertGroupComponent,
    AviAppLevelAlertComponent,
    AviAutoCompleteComponent,
    AviCheckboxContainerGroupComponent,
    AviCollectionDropdownComponent,
    AviCollectionDropdownSelectedValuesContainerComponent,
    AviContentLoadingContainerComponent,
    AviDropdownButtonComponent,
    AviDropdownButtonItemComponent,
    AviDropdownButtonListComponent,
    AviIconDirective,
    AviPageHeaderComponent,
    AviRepeatedStringsComponent,
    AviSliderComponent,
    AviStatePageComponent,
    AviTextareaContainerComponent,
    BooleanLabelPipe,
    BooleanToClarityStatusPipe,
    ButtonLoaderComponent,
    CapitalizePipe,
    CopyrightComponent,
    EnumValueLabelPipe,
    ExpanderActionConfirmationComponent,
    ExpanderActionRowComponent,
    ExpanderButtonComponent,
    ExpanderComponent,
    ExpanderWithActionsComponent,
    FileUploadComponent,
    FileUploadTextareaComponent,
    FormatBytesPipe,
    FormatJsonPipe,
    FullModalBreadcrumbComponent,
    FullModalBreadcrumbsComponent,
    FullModalComponent,
    FullModalConfigComponent,
    FullModalConfigErrorsComponent,
    FullModalConfigFooterComponent,
    FullModalConfigHeaderComponent,
    FullModalConfigHeaderTabsComponent,
    FullModalConfigViewerComponent,
    FullModalConfigWarningsComponent,
    FullModalPreviewComponent,
    FullModalTabSectionComponent,
    GeoLocationStringPipe,
    GradientLoaderComponent,
    HealthScoreClassNamePipe,
    ItemPreviewDefaultsComponent,
    ItemPreviewWrapperComponent,
    NamePipe,
    Num2abbrStrPipe,
    PreviewFieldComponent,
    SlugPipe,
    SubnetStringPipe,
    ToStringPipe,
    TrimSingleQuotePipe,
    TriSwitchComponent,
} from '.';

const sharedComponents = [
    AsyncFileUploadComponent,
    AsyncFileUploadWithUploadServiceComponent,
    AviAppLevelAlertComponent,
    AviAlertGroupComponent,
    AviCheckboxContainerGroupComponent,
    AviCollectionDropdownComponent,
    AviCollectionDropdownSelectedValuesContainerComponent,
    AviPageHeaderComponent,
    AviDropdownButtonComponent,
    AviDropdownButtonItemComponent,
    AviDropdownButtonListComponent,
    AviRepeatedStringsComponent,
    AviSliderComponent,
    AviStatePageComponent,
    AviTextareaContainerComponent,
    AviAutoCompleteComponent,
    AviContentLoadingContainerComponent,
    ButtonLoaderComponent,
    CopyrightComponent,
    ExpanderActionConfirmationComponent,
    ExpanderActionRowComponent,
    ExpanderButtonComponent,
    ExpanderComponent,
    ExpanderWithActionsComponent,
    FileUploadComponent,
    FileUploadTextareaComponent,
    FullModalBreadcrumbComponent,
    FullModalBreadcrumbsComponent,
    FullModalComponent,
    FullModalConfigComponent,
    FullModalConfigErrorsComponent,
    FullModalConfigFooterComponent,
    FullModalConfigHeaderComponent,
    FullModalConfigHeaderTabsComponent,
    FullModalConfigViewerComponent,
    FullModalConfigWarningsComponent,
    FullModalPreviewComponent,
    FullModalTabSectionComponent,
    GradientLoaderComponent,
    ItemPreviewDefaultsComponent,
    ItemPreviewWrapperComponent,
    PreviewFieldComponent,
    TriSwitchComponent,
];

const sharedDirectives = [
    AviIconDirective,
    BooleanColorDirective,
    InputFieldDirective,
    SetBreadcrumbDescriptionDirective,
];

const sharedPipes = [
    BooleanLabelPipe,
    BooleanToClarityStatusPipe,
    CapitalizePipe,
    EnumValueLabelPipe,
    FormatBytesPipe,
    FormatJsonPipe,
    GeoLocationStringPipe,
    HealthScoreClassNamePipe,
    NamePipe,
    Num2abbrStrPipe,
    SlugPipe,
    ToStringPipe,
    SubnetStringPipe,
    TrimSingleQuotePipe,
];

const aviCommonLibModules = [
    AviPipesModule,
    AviTooltipModule,
];

@NgModule({
    declarations: [
        ...sharedComponents,
        ...sharedDirectives,
        ...sharedPipes,
    ],
    exports: [
        ...sharedPipes,
        ...aviCommonLibModules,
        AsyncFileUploadComponent,
        AsyncFileUploadWithUploadServiceComponent,
        AviAutoCompleteComponent,
        AviAlertGroupComponent,
        AviCheckboxContainerGroupComponent,
        AviCollectionDropdownComponent,
        AviContentLoadingContainerComponent,
        AviPageHeaderComponent,
        AviDropdownButtonComponent,
        AviDropdownComponent,
        AviDropdownOptionsComponent,
        AviIconDirective,
        AviInputContainerComponent,
        AviRepeatedStringsComponent,
        AviSliderComponent,
        AviStatePageComponent,
        AviTextareaContainerComponent,
        BooleanColorDirective,
        ButtonLoaderComponent,
        CommonModule,
        CopyrightComponent,
        ExpanderComponent,
        ExpanderWithActionsComponent,
        FileUploadComponent,
        FileUploadTextareaComponent,
        FormsModule,
        FullModalComponent,
        FullModalConfigComponent,
        FullModalConfigViewerComponent,
        FullModalPreviewComponent,
        FullModalTabSectionComponent,
        GradientLoaderComponent,
        InputFieldDirective,
        ItemPreviewDefaultsComponent,
        ItemPreviewWrapperComponent,
        PreviewFieldComponent,
        SetBreadcrumbDescriptionDirective,
        TooltipModule,
        TriSwitchComponent,
        VIPModule,
    ],
    imports: [
        ...aviCommonLibModules,
        A11yModule,
        AviRadioModule,
        ClrAlertModule,
        ClrCheckboxModule,
        ClrDatalistModule,
        ClrDropdownModule,
        ClrIconModule,
        ClrInputModule,
        ClrProgressBarModule,
        ClrRadioModule,
        ClrSpinnerModule,
        CommonModule,
        FormsModule,
        OverlayModule,
        LetModule,
        PushModule,
        RightRailModule,
        ScrollingModule,
        TooltipModule,
        VIPModule,
        AviInputContainerComponent,
        AviDropdownComponent,
        AviDropdownOptionsComponent,
        AviDropdownSingleSelectedValueComponent,
        TimeframeSelectorComponent,
        MetricValuesSelectorComponent,
    ],
    providers: [
        BooleanLabelPipe,
        FormatBytesPipe,
        CapitalizePipe,
        NamePipe,
        SlugPipe,
        FormatJsonPipe,
        SubnetStringPipe,
        EnumValueLabelPipe,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class SharedModule {}
